import { AlertTriangle, MessageSquare, Phone } from 'lucide-react';
import { soloGateApplies, coiDueInfo, insuranceStatus, hasInsuranceOnFile, fmtPolicyDate } from '../../lib/soloGate';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_LESSEES } from '../../lib/demoData';
import type { UserDoc, UserStatus } from '../../lib/types';
import { PageHeader } from '../../components/PageHeader';

/** Row label in the lessee card's fact list — uppercase, muted, never bold. */
function Dt({ children }: { children: React.ReactNode }) {
  return <dt className="text-[11px] uppercase tracking-[0.07em] text-ink-subtle sm:pt-0.5">{children}</dt>;
}
import { Spinner } from '../../components/Spinner';
import { TRACKED_REG } from '../../lib/flightTrack';
import { OWNER_FIRST } from '../../lib/identity';
import { proofBeforeAnyFlight } from '../../lib/onboarding';

const STATUS_LABEL: Record<UserStatus, string> = {
  pending_approval: 'Pending approval',
  profile_incomplete: 'Profile incomplete',
  verification_pending: 'Verification pending',
  active: 'Active',
  suspended: 'Suspended',
  terminated: 'Terminated',
};

const STATUS_BADGE: Record<UserStatus, string> = {
  pending_approval: 'badge-warning',
  profile_incomplete: 'badge-info',
  verification_pending: 'badge-warning',
  active: 'badge-success',
  suspended: 'badge-neutral',
  terminated: 'badge-danger',
};

// The onboarding items we track per pilot. `done(u, docs)` reads the pilot's
// profile fields + which document photos they've uploaded (docs = the set of
// DocumentType strings on file). Anything not done is shown as "still needs".
const DISCLOSURE_KEYS = ['priorAccident', 'priorEnforcement', 'priorInsuranceRefusal', 'dui', 'pendingActions'] as const;
const ONBOARDING_ITEMS: {
  label: string;
  /** Shown instead of `label` for a checkout CFI, who needs a different product. */
  cfiLabel?: string;
  /** Skipped entirely for a CFI — obligations that only attach to a lessee. */
  lesseeOnly?: boolean;
  /** Overrides `label`/`cfiLabel` when the wording depends on the pilot's record. */
  labelFor?: (u: UserDoc) => string | null;
  done: (u: UserDoc, docs: Set<string>) => boolean;
}[] = [
  {
    label: 'Personal & emergency contact',
    done: (u) => {
      const p = u.personal ?? {};
      return Boolean(
        p.firstName && p.lastName && p.dateOfBirth && p.phone && p.address &&
        p.emergencyContact?.name && p.emergencyContact?.phone
      );
    },
  },
  { label: 'Pilot certificate', done: (u, d) => Boolean(u.pilot?.certificateType && u.pilot?.certificateNumber && d.has('pilot_certificate')) },
  { label: 'Medical certificate', done: (u, d) => Boolean(u.pilot?.medicalClass && u.pilot?.medicalExpiration && d.has('medical')) },
  { label: 'Flight time', done: (u) => Boolean(u.pilot?.totalHours) },
  { label: 'Government ID', done: (u, d) => Boolean(d.has('drivers_license') && u.onboardingDraft?.licenseExp) },
  { label: 'Background questions', done: (u) => DISCLOSURE_KEYS.every((k) => Boolean(u.disclosures?.[k]?.answer)) },
  { label: 'Payment card on file', done: (u) => Boolean(u.paymentMethodOnFile), lesseeOnly: true },
  {
    label: "Renter's insurance",
    cfiLabel: 'CFI insurance certificate',
    // A lapsed policy is NOT "done" — it goes back on the waiting list, by name, so
    // the card can never read "onboarding complete" over an expired certificate.
    labelFor: (u) => {
      const s = insuranceStatus(u);
      return s.state === 'expired' ? `Renter's insurance — EXPIRED ${fmtPolicyDate(s.exp)}` : null;
    },
    done: (u, d) => hasInsuranceOnFile(u) && d.has('renters_insurance'),
  },
];

function missingItems(u: UserDoc, docs: Set<string>): string[] {
  const isCfi = u.billingProfile === 'cfi';
  return ONBOARDING_ITEMS.filter((it) => !(isCfi && it.lesseeOnly))
    .filter((it) => !it.done(u, docs))
    .map((it) => it.labelFor?.(u) ?? (isCfi && it.cfiLabel ? it.cfiLabel : it.label));
}

const EMPTY_DOCS: Set<string> = new Set();
// In demo mode there's no documents collection, so assume photos are on file
// (demo lessees are pre-populated) and let the profile fields drive the list.
const DEMO_DOCS: Set<string> = new Set(['pilot_certificate', 'medical', 'drivers_license', 'renters_insurance', 'lgb_badge']);

export function AdminLessees() {
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter');
  const [users, setUsers] = useState<UserDoc[] | null>(null);
  const { demoMode, viewAs } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (demoMode) {
      setUsers(DEMO_LESSEES);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'users'), orderBy('createdAt', 'desc')),
      (snap) => setUsers(snap.docs.map((d) => d.data() as UserDoc))
    );
    return () => unsub();
  }, [demoMode]);

  // Which document photos each pilot has uploaded (ownerUid -> set of DocumentType),
  // so the "still needs" checklist knows whether the certificate/medical/ID/insurance
  // images are actually on file — not just the typed fields.
  const [docsByUid, setDocsByUid] = useState<Record<string, Set<string>>>({});
  useEffect(() => {
    if (demoMode || !db) return;
    const unsub = onSnapshot(collection(db, 'documents'), (snap) => {
      const m: Record<string, Set<string>> = {};
      snap.forEach((d) => {
        const data = d.data() as { ownerUid?: string; type?: string };
        if (!data.ownerUid || !data.type) return;
        (m[data.ownerUid] ??= new Set()).add(data.type);
      });
      setDocsByUid(m);
    });
    return () => unsub();
  }, [demoMode]);

  const filtered = useMemo(() => {
    if (!users) return null;
    if (filter === 'pending') return users.filter((u) => u.status === 'pending_approval');
    if (filter === 'verification')
      return users.filter((u) => u.status === 'verification_pending');
    return users.filter((u) => u.role === 'lessee');
  }, [users, filter]);

  async function setStatus(u: UserDoc, status: UserStatus, extra: Record<string, unknown> = {}) {
    if (demoMode) {
      setUsers((curr) =>
        (curr ?? []).map((x) => (x.uid === u.uid ? { ...x, status, ...extra } : x))
      );
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'users', u.uid), { status, ...extra });
  }

  // The lessee's pricing plan: 'standard' ($100/hr, no minimum), 'promo'
  // ($75/hr, no minimum, month-to-month — the friends & family / promo-code deal),
  // or 'comped' (owner's guest — flights recorded but never charged).
  async function setPlan(u: UserDoc, plan: 'standard' | 'promo' | 'comped') {
    if (demoMode) {
      setUsers((curr) => (curr ?? []).map((x) => (x.uid === u.uid ? { ...x, plan } : x)));
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'users', u.uid), { plan });
  }

  // Designate an instructor: instructor plan (their own flying bills at their plan —
  // set Plan to Promo for $75/hr — no monthly minimum). Checkouts are not free: the
  // renter being checked out logs and pays that flight. Toggling off = lessee.
  async function setCfi(u: UserDoc, isCfi: boolean) {
    const billingProfile: UserDoc['billingProfile'] = isCfi ? 'cfi' : 'lessee';
    if (demoMode) {
      setUsers((curr) => (curr ?? []).map((x) => (x.uid === u.uid ? { ...x, billingProfile } : x)));
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'users', u.uid), { billingProfile });
  }

  async function approve(u: UserDoc) {
    await setStatus(u, 'profile_incomplete', { approvedAt: demoMode ? new Date() : serverTimestamp() });
  }
  async function reject(u: UserDoc) {
    if (!confirm(`Reject ${u.email}? They will be unable to access the portal.`)) return;
    await setStatus(u, 'terminated', { terminatedAt: demoMode ? new Date() : serverTimestamp() });
  }
  async function activate(u: UserDoc) {
    // Casey 11/3/26: no pilot (renter, student or CFI) goes active without their own
    // insurance on file. Same gate as the pilot's page.
    if (!hasInsuranceOnFile(u)) {
      alert(`${u.email}: insurance certificate isn’t on file yet. Add it on their page (AJM and Casey named), then activate.`);
      return;
    }
    // No pilot email here. Onboarding sends exactly one, the welcome, and only once
    // the account is active AND the signed lease is recorded (functions onPilotActivated).
    if (
      !confirm(
        u.leaseSignedAt
          ? `Activate ${u.email}? Their lease is on file, so they get the welcome email and can book.`
          : `Activate ${u.email}? No email yet. Booking stays locked until the signed lease is ` +
              'recorded. Use "DocuSign is back. Approve fully" on their page — that sends the one welcome email.'
      )
    )
      return;
    await setStatus(u, 'active');
  }
  async function suspend(u: UserDoc) {
    if (!confirm(`Suspend ${u.email}? They will be blocked from booking and flying.`)) return;
    await setStatus(u, 'suspended');
  }

  return (
    <>
      <PageHeader
        title="Lessees"
        subtitle="Approve registrations, verify documents, manage status."
        actions={
          <div className="flex gap-2 text-sm">
            <FilterBtn current={filter} value={null} onClick={() => setParams({})}>
              All
            </FilterBtn>
            <FilterBtn
              current={filter}
              value="pending"
              onClick={() => setParams({ filter: 'pending' })}
            >
              Pending approval
            </FilterBtn>
            <FilterBtn
              current={filter}
              value="verification"
              onClick={() => setParams({ filter: 'verification' })}
            >
              Verification pending
            </FilterBtn>
          </div>
        }
      />

      {!filtered ? (
        <Spinner label="Loading lessees" />
      ) : filtered.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">
          No lessees to show. Share your registration PIN to start.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((u) => {
            const docs = demoMode ? DEMO_DOCS : docsByUid[u.uid] ?? EMPTY_DOCS;
            const missing = missingItems(u, docs);
            const ins = insuranceStatus(u);
            // Owner excluded: AJM is the Named Insured on the hull policy, not a renter.
            const insuranceApplies = u.role === 'lessee' && u.billingProfile !== 'owner';
            return (
            <div key={u.uid} className="card overflow-hidden p-0">
              {/* Dual-instruction-only is where every new renter STARTS — flagging it red
                  would put an alarm on a pilot who has done nothing wrong. The state
                  lives in the fact list below as plain information. The strip is kept
                  for the one case that is genuinely wrong: the certificate is past the
                  14-day backstop. Rare, so it still means something when you see it. */}
              {insuranceApplies && ins.state === 'expired' ? (
                <div className="flex items-center gap-2 bg-[#B42318] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-white">
                  <AlertTriangle size={13} className="shrink-0" />
                  Insurance expired {fmtPolicyDate(ins.exp)}
                  {u.billingProfile === 'cfi' ? '' : ' · no solo PIC'}
                </div>
              ) : soloGateApplies(u) && coiDueInfo(u.leaseEffectiveDate)?.overdue ? (
                <div className="flex items-center gap-2 bg-[#B42318] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-white">
                  <AlertTriangle size={13} className="shrink-0" />
                  Insurance certificate overdue · no solo PIC
                </div>
              ) : insuranceApplies && ins.state === 'expiring' ? (
                <div className="flex items-center gap-2 bg-[#B54708] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-white">
                  <AlertTriangle size={13} className="shrink-0" />
                  Insurance expires {fmtPolicyDate(ins.exp)} · {ins.days === 0 ? 'today' : `${ins.days} day${ins.days === 1 ? '' : 's'}`}
                </div>
              ) : null}

              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/admin/lessees/${u.uid}`} className="group min-w-0">
                    <div className="font-medium text-navy-900 group-hover:underline">
                      {u.personal?.firstName} {u.personal?.lastName}
                    </div>
                    <div className="truncate text-xs text-ink-muted">
                      {u.email}
                      {u.personal?.phone && ` · ${u.personal.phone}`}
                    </div>
                  </Link>
                  {/* "Active" alone read as "can book" for a pilot whose lease wasn't recorded. */}
                  <span
                    className={`${u.status === 'active' && !u.leaseSignedAt ? 'badge-warning' : STATUS_BADGE[u.status]} shrink-0 whitespace-nowrap`}
                  >
                    {u.status === 'active' && !u.leaseSignedAt ? 'Active · no lease' : STATUS_LABEL[u.status]}
                  </span>
                </div>

                {u.personal?.phone && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <a
                      href={`sms:${u.personal.phone.replace(/[^0-9+]/g, '')}?&body=${encodeURIComponent(
                        `Hi ${u.personal.firstName || 'there'}, it's ${OWNER_FIRST} with AJM Aviation (${TRACKED_REG}). `
                      )}`}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-navy-700 ring-1 ring-inset ring-navy-900/15 hover:bg-sky-50"
                    >
                      <MessageSquare size={12} /> Text
                    </a>
                    <a
                      href={`tel:${u.personal.phone.replace(/[^0-9+]/g, '')}`}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-navy-700 ring-1 ring-inset ring-navy-900/15 hover:bg-sky-50"
                    >
                      <Phone size={12} /> Call
                    </a>
                  </div>
                )}

                {/* Label/value rows instead of tinted boxes. Each fact appears once. */}
                <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[128px_1fr]">
                  {soloGateApplies(u) && (
                    <>
                      <Dt>Can fly</Dt>
                      <dd className="text-ink">
                        {proofBeforeAnyFlight(u)
                          ? ins.state === 'expired'
                            ? `Not cleared to fly — renter's insurance expired ${fmtPolicyDate(ins.exp)} (lease §7.5)`
                            : 'Not cleared to fly — no certificate of insurance on file yet (lease §7.3)'
                          : ins.state === 'expired'
                          ? `Dual instruction only — renter's insurance expired ${fmtPolicyDate(ins.exp)} (§11.2)`
                          : 'Dual instruction only — no certificate of insurance on file yet (§11.2)'}
                      </dd>
                      <Dt>Certificate due</Dt>
                      <dd className="text-ink">
                        {(() => {
                          const c = coiDueInfo(u.leaseEffectiveDate);
                          if (!c)
                            return (
                              <span className="text-ink-muted">
                                Clock not started — no lease effective date
                              </span>
                            );
                          const n = Math.abs(c.daysLeft);
                          return c.overdue ? (
                            <span className="font-medium text-[#B42318]">
                              {c.dueDate} — {n} day{n === 1 ? '' : 's'} overdue
                            </span>
                          ) : (
                            <>
                              {c.dueDate}{' '}
                              <span className="text-ink-muted">
                                ({c.daysLeft} day{c.daysLeft === 1 ? '' : 's'} left)
                              </span>
                            </>
                          );
                        })()}
                      </dd>
                      <Dt>Acknowledged</Dt>
                      <dd className={u.soloNoticeAckAt ? 'text-ink' : 'text-ink-muted'}>
                        {u.soloNoticeAckAt
                          ? `Yes · ${u.soloNoticeAckAt.toDate().toLocaleDateString()}`
                          : 'Not yet, in the app'}
                      </dd>
                    </>
                  )}

                  {insuranceApplies && (
                    <>
                      <Dt>Insurance</Dt>
                      <dd
                        className={
                          ins.state === 'expired'
                            ? 'font-medium text-[#B42318]'
                            : ins.state === 'expiring'
                            ? 'font-medium text-[#B54708]'
                            : ins.state === 'none'
                            ? 'text-ink-muted'
                            : 'text-ink'
                        }
                      >
                        {ins.state === 'none' ? (
                          'None on file'
                        ) : (
                          <>
                            {u.insurance?.carrier} #{u.insurance?.policyNumber}
                            {u.insurance?.liabilityLimit ? ` · $${u.insurance.liabilityLimit.toLocaleString()} liability` : ''}
                            {u.insurance?.aircraftDamageLimit ? ` · $${u.insurance.aircraftDamageLimit.toLocaleString()} aircraft damage` : ''}
                            {' · '}
                            {ins.state === 'expired'
                              ? `EXPIRED ${fmtPolicyDate(ins.exp)}`
                              : `expires ${fmtPolicyDate(ins.exp)}${
                                  ins.state === 'expiring' ? ` (${ins.days === 0 ? 'today' : `${ins.days} day${ins.days === 1 ? '' : 's'}`})` : ''
                                }`}
                            {u.insurance?.notes ? <span className="block text-xs text-ink-muted">{u.insurance.notes}</span> : null}
                          </>
                        )}
                      </dd>
                    </>
                  )}

                  {u.policyNamed && (
                    <>
                      <Dt>Aircraft policy</Dt>
                      <dd className="text-ink">
                        Named {u.policyNamed.as === 'both' ? 'pilot & instructor' : u.policyNamed.as === 'instructor' ? 'instructor' : 'pilot'} on the aircraft policy
                        {u.policyNamed.since ? ` since ${fmtPolicyDate(u.policyNamed.since)}` : ''}
                        {u.policyNamed.policyNumber ? ` · ${u.policyNamed.policyNumber}` : ''}
                        {u.policyNamed.through ? ` · through ${fmtPolicyDate(u.policyNamed.through)}` : ''}
                        <span className="block text-xs text-ink-muted">Renter’s insurance is still required (§11.2).</span>
                      </dd>
                    </>
                  )}

                  <Dt>Card on file</Dt>
                  <dd className={u.paymentMethodOnFile ? 'text-ink' : 'text-ink-muted'}>
                    {u.paymentMethodOnFile
                      ? u.cardBrand && u.cardLast4
                        ? `${u.cardBrand.charAt(0).toUpperCase()}${u.cardBrand.slice(1)} ····${u.cardLast4}`
                        : 'Yes'
                      : 'None'}
                  </dd>

                  <Dt>Waiting on</Dt>
                  <dd className={missing.length ? 'text-ink' : 'text-ink-muted'}>
                    {missing.length ? missing.join(' · ') : 'Nothing — onboarding complete'}
                  </dd>
                </dl>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3">
                <label className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                  Plan
                  <select
                    className="rounded border border-slate-200 bg-white px-1.5 py-1 text-xs text-navy-900"
                    value={u.plan ?? 'standard'}
                    onChange={(e) => setPlan(u, e.target.value as 'standard' | 'promo' | 'comped')}
                    title="Standard = $100/hr, no minimum, month-to-month. Promo = $75/hr, no minimum, month-to-month. Comped = owner's guest, flights recorded but never charged."
                  >
                    <option value="standard">Standard</option>
                    <option value="promo">Promo ($75/hr)</option>
                    <option value="comped">Comped (free)</option>
                  </select>
                </label>

                <label
                  className="inline-flex items-center gap-1.5 text-xs text-ink-muted"
                  title="Instructor: their own flying bills at their plan (set Plan to Promo for $75/hr), no monthly minimum. Checkouts are the renter's flight — the renter logs and pays it."
                >
                  <input
                    type="checkbox"
                    checked={u.billingProfile === 'cfi'}
                    onChange={(e) => setCfi(u, e.target.checked)}
                  />
                  CFI (instructor plan)
                </label>

                <div className="ml-auto flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      viewAs(u.uid);
                      nav('/');
                    }}
                    className="btn-secondary py-1.5 px-3 text-xs min-h-0"
                    title="See the app exactly as this pilot sees it (read-only)"
                  >
                    View as
                  </button>
                  {u.status === 'pending_approval' && (
                    <>
                      <button onClick={() => approve(u)} className="btn-primary py-1.5 px-3 text-xs min-h-0">
                        Approve
                      </button>
                      <button onClick={() => reject(u)} className="btn-danger py-1.5 px-3 text-xs min-h-0">
                        Reject
                      </button>
                    </>
                  )}
                  {u.status === 'verification_pending' && (
                    <button onClick={() => activate(u)} className="btn-primary py-1.5 px-3 text-xs min-h-0">
                      Activate
                    </button>
                  )}
                  {u.status === 'active' && (
                    <button onClick={() => suspend(u)} className="btn-secondary py-1.5 px-3 text-xs min-h-0">
                      Suspend
                    </button>
                  )}
                  {u.status === 'suspended' && (
                    <button onClick={() => activate(u)} className="btn-secondary py-1.5 px-3 text-xs min-h-0">
                      Reactivate
                    </button>
                  )}
                  <Link to={`/admin/lessees/${u.uid}`} className="btn-secondary py-1.5 px-3 text-xs min-h-0">
                    View
                  </Link>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function FilterBtn({
  current,
  value,
  onClick,
  children,
}: {
  current: string | null;
  value: string | null;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 ${
        active ? 'bg-brand text-white' : 'bg-white text-navy-900 ring-1 ring-inset ring-navy-900/15'
      }`}
    >
      {children}
    </button>
  );
}
